import { useEffect, useState } from 'react';
import { logEvent } from '../../utils/analytics-utils';
import { useConfig } from '../../components/context/ConfigContext';
import useCallerIsAdmin from './useCallerIsAdmin';
/**
 *
 *  This is hook is responsible for initializing application data.
 *  Any data that the application needs before the application loads goes here.
 *  For example, global WebUI(backend call) configuration, Guided onboarding, workspace and ACL information.
 *  Tipically, data that is shared throughout the application.
 *  Once you get data from the backend, we use React Context to store our shared data.
 *  From there, use Context hooks to get data from the application state.
 */

const useInitializeApplication = () => {
  const [hasFinishedInitializing, setHasFinishedInitializing] = useState<boolean>(false);
  const [hasInitializingError, setHasInitializingError] = useState<boolean>(false);

  // Hooks to get data. The hooks below are wrappers for React-Query.
  // We don't have support none React-Query calls yet.
  const { config, isLoading, hasError, error } = useConfig();
  const { clusterAdminInfoState } = useCallerIsAdmin();

  // States to watch out for.
  // TODO: Do we need .data for checking? Let's find out later.
  const successStates = [!isLoading, config, clusterAdminInfoState.data, clusterAdminInfoState.isSuccess];

  const errorStates = [hasError, clusterAdminInfoState.error];

  useEffect(() => {
    // Let's check to see if we loaded all the data
    if ([...successStates].every((value) => value)) {
      setHasFinishedInitializing(true);
      setHasInitializingError(false);
    }

    // If we get any errors from the list, we error out.
    if (errorStates.includes(true)) {
      setHasInitializingError(true);
      setHasFinishedInitializing(false);
      logEvent('WebUI Initialization failed');
    }
  }, [...successStates, ...errorStates]);

  return { hasFinishedInitializing, hasInitializingError };
};

export default useInitializeApplication;
